import { csvLiteral, normalizeDigits, type CsvLiteral } from './datamove.js';

/**
 * أرقام الهواتف.
 *
 * الرقم نصّ لا عدد، وهذه ليست تفصيلة: إكسل يرى عمودًا من الأرقام فيحوّله
 * إلى عدد، فيصير 201010001234 هو 2.0101E+11 — ستّ خانات معنوية والباقي
 * ضاع إلى الأبد. الملف يُحفظ، فتُستورد الأرقام مشوَّهة ولا يُكتشف الأمر
 * إلا حين يتصل أحد فلا يجد أحدًا.
 *
 * فالحماية هنا في ثلاثة مواضع:
 *
 *   عند التصدير — تُكتب الخلية بصيغة يفهمها إكسل نصًّا فلا يحوّلها.
 *   عند الاستيراد — تُرفض القيمة المشوَّهة صراحةً بدل حفظها.
 *   عند التخزين — يُوحَّد الشكل، ويُفصل مفتاح الدولة عن الرقم.
 */

/* ==========================================================================
   مفاتيح الدول
   ========================================================================== */

export interface CountryCodeDef {
  /** المفتاح بلا علامة زائد. */
  code: string;
  label: string;
  /** علم الدولة رمزًا تعبيريًّا — به تُختصر قائمة الاختيار. */
  flag: string;
  /** طول الرقم المحلي بعد إسقاط الصفر، للتحقق التقريبي. */
  nationalLength: readonly number[];
}

/**
 * قائمة مختصرة لا شاملة.
 *
 * القائمة الكاملة مئتا دولة لا تُعرض في قائمة اختيار، والمستأجر هنا عربي
 * في الغالب. وما ليس فيها يُكتب بمفتاحه كاملًا ويُقبل كما هو.
 */
export const COUNTRY_CODES: readonly CountryCodeDef[] = [
  { code: '20', label: 'مصر', flag: '🇪🇬', nationalLength: [10] },
  { code: '966', label: 'السعودية', flag: '🇸🇦', nationalLength: [9] },
  { code: '971', label: 'الإمارات', flag: '🇦🇪', nationalLength: [9] },
  { code: '965', label: 'الكويت', flag: '🇰🇼', nationalLength: [8] },
  { code: '974', label: 'قطر', flag: '🇶🇦', nationalLength: [8] },
  { code: '973', label: 'البحرين', flag: '🇧🇭', nationalLength: [8] },
  { code: '968', label: 'عُمان', flag: '🇴🇲', nationalLength: [8] },
  { code: '962', label: 'الأردن', flag: '🇯🇴', nationalLength: [9] },
  { code: '961', label: 'لبنان', flag: '🇱🇧', nationalLength: [7, 8] },
  { code: '964', label: 'العراق', flag: '🇮🇶', nationalLength: [10] },
  { code: '963', label: 'سوريا', flag: '🇸🇾', nationalLength: [9] },
  { code: '970', label: 'فلسطين', flag: '🇵🇸', nationalLength: [9] },
  { code: '212', label: 'المغرب', flag: '🇲🇦', nationalLength: [9] },
  { code: '213', label: 'الجزائر', flag: '🇩🇿', nationalLength: [9] },
  { code: '216', label: 'تونس', flag: '🇹🇳', nationalLength: [8] },
  { code: '218', label: 'ليبيا', flag: '🇱🇾', nationalLength: [9] },
  { code: '249', label: 'السودان', flag: '🇸🇩', nationalLength: [9] },
  { code: '967', label: 'اليمن', flag: '🇾🇪', nationalLength: [9] },
  { code: '90', label: 'تركيا', flag: '🇹🇷', nationalLength: [10] },
  { code: '44', label: 'المملكة المتحدة', flag: '🇬🇧', nationalLength: [10] },
  { code: '1', label: 'أمريكا وكندا', flag: '🇺🇸', nationalLength: [10] },
] as const;

export const COUNTRY_CODE_MAP = Object.fromEntries(
  COUNTRY_CODES.map((c) => [c.code, c]),
) as Readonly<Record<string, CountryCodeDef>>;

/** المفاتيح مرتَّبة بالأطول أولًا: 971 قبل 97 وقبل 9. */
const CODES_BY_LENGTH = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);

/* ==========================================================================
   الكشف عن التشويه
   ========================================================================== */

/**
 * الصيغة العلمية التي يُنتجها إكسل.
 *
 * مثل 2.0101E+11 أو 2.01117e+11. وجودها يعني أن الأرقام فُقدت فعلًا ولا
 * سبيل إلى استرجاعها من هذه القيمة: ستّ خانات معنوية بقيت والباقي أصفار.
 */
const SCIENTIFIC = /^\s*[-+]?\d+(?:[.,]\d+)?\s*[eE]\s*[-+]?\d+\s*$/;

export function isMangledNumber(raw: string): boolean {
  return SCIENTIFIC.test(raw);
}

/* ==========================================================================
   التوحيد
   ========================================================================== */

export interface PhoneParts {
  /** الشكل الموحَّد: مفتاح الدولة والرقم بلا فواصل، مسبوقًا بعلامة زائد. */
  e164: string;
  /** مفتاح الدولة بلا زائد، أو null إن لم يُعرف. */
  countryCode: string | null;
  /** الرقم المحلي بعد إسقاط المفتاح والصفر الأول. */
  national: string;
}

export interface PhoneResult {
  value: PhoneParts | null;
  /** سبب الرفض، أو null إن قُبل. */
  error: string | null;
}

/**
 * توحيد رقم هاتف.
 *
 * دالة خالصة: هي الحدّ الذي يقف عنده ما يُحفظ في عمود الهاتف، ويستعملها
 * الاستيراد والنموذج معًا فلا يفترق ما يُقبل من هنا عمّا يُقبل من هناك.
 *
 * والدولة الافتراضية تُستعمل حين يبدأ الرقم بصفر: 01012345678 في مصر هو
 * ‎+201012345678. وبلا دولة افتراضية يُقبل الرقم كما هو ولا يُخمَّن مفتاحه —
 * التخمين يضع أرقامًا في بلد غير بلدها.
 */
export function normalizePhone(raw: string, defaultCountry?: string | null): PhoneResult {
  const text = normalizeDigits(String(raw ?? '')).trim();
  if (!text) return { value: null, error: null };

  if (isMangledNumber(text)) {
    return {
      value: null,
      error:
        'الرقم بصيغة علمية مثل 2.01E+11، وهذا يعني أن إكسل حوّل العمود إلى عدد وفقد خاناته. ' +
        'نسّق عمود الهاتف كنصّ في الملف الأصلي ثم أعد الاستيراد.',
    };
  }

  /* ما ليس رقمًا ولا علامة زائد ولا فاصلًا معتادًا يُرفض. */
  if (/[^\d+\s()\-.ـ]/.test(text)) {
    return { value: null, error: `"${raw}" ليس رقم هاتف.` };
  }

  let digits = text.replace(/[\s()\-.ـ]/g, '');
  let hadPlus = digits.startsWith('+');
  if (hadPlus) digits = digits.slice(1);

  /* 00 بادئةً دوليةً تعادل علامة الزائد. */
  if (!hadPlus && digits.startsWith('00')) {
    digits = digits.slice(2);
    hadPlus = true;
  }

  if (!/^\d+$/.test(digits)) {
    return { value: null, error: `"${raw}" ليس رقم هاتف.` };
  }

  if (digits.length < 6) {
    return { value: null, error: `"${raw}" أقصر من أن يكون رقم هاتف.` };
  }
  if (digits.length > 15) {
    /* الحد الدولي خمس عشرة خانة — E.164. */
    return { value: null, error: `"${raw}" أطول من أي رقم هاتف.` };
  }

  const fallback = (defaultCountry ?? '').replace(/\D/g, '') || null;

  /* رقم محلي يبدأ بصفر: المفتاح من الإعدادات. */
  if (!hadPlus && digits.startsWith('0')) {
    const national = digits.replace(/^0+/, '');
    if (!fallback) {
      return {
        value: { e164: digits, countryCode: null, national: digits },
        error: null,
      };
    }
    return {
      value: { e164: `+${fallback}${national}`, countryCode: fallback, national },
      error: null,
    };
  }

  /* رقم بمفتاح صريح، أو بلا صفر ولا زائد. */
  const match = CODES_BY_LENGTH.find((c) => digits.startsWith(c.code));

  if (match) {
    const national = digits.slice(match.code.length).replace(/^0+/, '');
    return {
      value: { e164: `+${match.code}${national}`, countryCode: match.code, national },
      error: null,
    };
  }

  if (hadPlus) {
    /* مفتاح دولة خارج القائمة: يُقبل كما هو ولا يُفصل. */
    return { value: { e164: `+${digits}`, countryCode: null, national: digits }, error: null };
  }

  if (fallback) {
    return {
      value: { e164: `+${fallback}${digits}`, countryCode: fallback, national: digits },
      error: null,
    };
  }

  return { value: { e164: digits, countryCode: null, national: digits }, error: null };
}

/** الشكل المعروض: المفتاح ثم الرقم مفصولين بمسافة. */
export function formatPhone(stored: string | null | undefined): string {
  if (!stored) return '';
  const parsed = normalizePhone(stored);
  if (!parsed.value) return String(stored);

  const { countryCode, national } = parsed.value;
  return countryCode ? `+${countryCode} ${national}` : parsed.value.e164;
}

/* ==========================================================================
   خلية إكسل
   ========================================================================== */

/**
 * كتابة الرقم في خلية لا يحوّلها إكسل.
 *
 * الصيغة ="..." تجعل إكسل يقرأها نصًّا ثابتًا. وهي صيغة، لكنها آمنة هنا
 * وحدها: القيمة مُوحَّدة قبلها فلا تحوي إلا أرقامًا وعلامة زائد، فلا سبيل
 * لأن تصير أمرًا يُنفَّذ. وما فشل توحيدُه يُكتب نصًّا عاديًا ويتولاه حارس
 * الحقن العام.
 */
export function phoneCell(stored: string | null | undefined): string | CsvLiteral {
  if (!stored) return '';

  const parsed = normalizePhone(stored);
  if (!parsed.value) return String(stored);

  const safe = parsed.value.e164;
  /* ما لم يُوحَّد إلى أرقام وعلامة زائد يُترك لحارس الحقن العام. */
  if (!/^\+?\d+$/.test(safe)) return String(stored);

  return csvLiteral(`="${safe}"`);
}

/* ==========================================================================
   روابط التواصل
   ========================================================================== */

/**
 * رقم صالح لرابط واتساب `wa.me`.
 *
 * الرابط يشترط أرقامًا فقط بمفتاح الدولة وبلا علامة زائد ولا فواصل. ورقمٌ
 * بلا مفتاح دولة لا يصلح: `wa.me/1012345678` يذهب إلى رقم في بلد آخر أو
 * إلى لا شيء. فيُعاد `null` ويُخفي زرَّ المحادثة بدل أن يفتح محادثة خاطئة.
 *
 * وهذه ليست تكاملًا مع واتساب: لا رسائل تُرسَل من النظام ولا محادثات تُقرأ
 * فيه. هي إحالة إلى التطبيق على جهاز من ضغط الزر — والفرق مهم لمن يقرأ
 * الشيفرة باحثًا عن ربط قنوات ميتا.
 */
export function whatsappNumber(stored: string | null | undefined): string | null {
  if (!stored) return null;

  const parsed = normalizePhone(stored);
  if (!parsed.value || !parsed.value.countryCode) return null;

  const digits = parsed.value.e164.replace(/\D/g, '');
  return digits.length >= 8 ? digits : null;
}
